import { useState, useEffect, useCallback } from 'react';
import {
  Box, Heading, Text, SimpleGrid, Stat, StatLabel, StatNumber, StatHelpText,
  Card, CardBody, Table, Thead, Tbody, Tr, Th, Td, Flex, Spinner, Badge, useBreakpointValue,
} from '@chakra-ui/react';
import { apiService } from '../services/apiService';

export default function Statistiques() {
  const isMobile = useBreakpointValue({ base: true, md: false });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiService.makeRequest('/analytics/stats');
      setStats(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  if (loading) return <Flex justify="center" py={20}><Spinner size="xl" /></Flex>;
  if (error) return <Text color="red.500" textAlign="center" py={10}>{error}</Text>;
  if (!stats) return null;

  const maxParJour = Math.max(1, ...stats.connexions_par_jour.map(j => j.total));

  return (
    <Box maxW="1100px" mx="auto">
      <Heading size="lg" mb={2}>Statistiques d'utilisation</Heading>
      <Text color="gray.500" mb={6}>Connexions et visites de pages de l'application (données internes).</Text>

      <SimpleGrid columns={{ base: 2, md: 5 }} spacing={4} mb={8}>
        <Stat bg="white" p={4} borderRadius="md" shadow="sm">
          <StatLabel>Connexions aujourd'hui</StatLabel>
          <StatNumber color="green.600">{stats.connexions_aujourdhui}</StatNumber>
        </Stat>
        <Stat bg="white" p={4} borderRadius="md" shadow="sm">
          <StatLabel>Connexions 7 jours</StatLabel>
          <StatNumber color="green.600">{stats.connexions_7j}</StatNumber>
        </Stat>
        <Stat bg="white" p={4} borderRadius="md" shadow="sm">
          <StatLabel>Connexions 30 jours</StatLabel>
          <StatNumber color="green.600">{stats.connexions_30j}</StatNumber>
        </Stat>
        <Stat bg="white" p={4} borderRadius="md" shadow="sm">
          <StatLabel>Membres actifs (30j)</StatLabel>
          <StatNumber color="blue.600">{stats.utilisateurs_uniques_30j}</StatNumber>
          <StatHelpText>utilisateurs uniques</StatHelpText>
        </Stat>
        <Stat bg="white" p={4} borderRadius="md" shadow="sm">
          <StatLabel>Pages vues aujourd'hui</StatLabel>
          <StatNumber color="purple.600">{stats.visites_aujourdhui}</StatNumber>
        </Stat>
      </SimpleGrid>

      {/* Connexions par jour (14 derniers jours) */}
      <Card mb={8}>
        <CardBody>
          <Heading size="md" mb={4}>Connexions — 14 derniers jours</Heading>
          <Flex align="flex-end" gap={{ base: 1, md: 2 }} h="140px">
            {stats.connexions_par_jour.map(j => (
              <Flex key={j.jour} direction="column" align="center" flex={1}>
                <Text fontSize="xs" color="gray.500" mb={1}>{j.total}</Text>
                <Box
                  w="100%"
                  bg="green.400"
                  borderRadius="4px 4px 0 0"
                  h={`${Math.max(4, (j.total / maxParJour) * 100)}px`}
                />
                <Text fontSize="10px" color="gray.400" mt={1}>
                  {new Date(j.jour).toLocaleDateString('fr-FR', isMobile ? { day: '2-digit' } : { day: '2-digit', month: '2-digit' })}
                </Text>
              </Flex>
            ))}
          </Flex>
        </CardBody>
      </Card>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mb={8}>
        {/* Pages les plus visitées */}
        <Card>
          <CardBody>
            <Heading size="md" mb={4}>Pages les plus visitées (30j)</Heading>
            <Table size="sm">
              <Thead><Tr><Th>Page</Th><Th isNumeric>Visites</Th></Tr></Thead>
              <Tbody>
                {stats.pages_plus_visitees.map(p => (
                  <Tr key={p.chemin}>
                    <Td fontFamily="mono" fontSize="xs">{p.chemin}</Td>
                    <Td isNumeric>{p.total}</Td>
                  </Tr>
                ))}
                {stats.pages_plus_visitees.length === 0 && (
                  <Tr><Td colSpan={2}><Text color="gray.500" textAlign="center">Aucune donnée</Text></Td></Tr>
                )}
              </Tbody>
            </Table>
          </CardBody>
        </Card>

        {/* Utilisateurs les plus actifs */}
        <Card>
          <CardBody>
            <Heading size="md" mb={4}>Membres les plus actifs (30j)</Heading>
            <Table size="sm">
              <Thead><Tr><Th>Membre</Th><Th isNumeric>Connexions</Th></Tr></Thead>
              <Tbody>
                {stats.utilisateurs_actifs.map(u => (
                  <Tr key={u.user_id}>
                    <Td>{u.prenom} {u.nom}</Td>
                    <Td isNumeric>{u.total_connexions}</Td>
                  </Tr>
                ))}
                {stats.utilisateurs_actifs.length === 0 && (
                  <Tr><Td colSpan={2}><Text color="gray.500" textAlign="center">Aucune donnée</Text></Td></Tr>
                )}
              </Tbody>
            </Table>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Dernières connexions */}
      <Card>
        <CardBody>
          <Heading size="md" mb={4}>Dernières connexions</Heading>
          <Box overflowX="auto">
            <Table size="sm">
              <Thead><Tr><Th>Membre</Th><Th>Date</Th><Th display={{ base: 'none', md: 'table-cell' }}>IP</Th><Th display={{ base: 'none', md: 'table-cell' }}>Appareil</Th></Tr></Thead>
              <Tbody>
                {stats.dernieres_connexions.map((c, i) => (
                  <Tr key={i}>
                    <Td>{c.prenom} {c.nom}</Td>
                    <Td fontSize="xs">{new Date(c.date_connexion).toLocaleString('fr-FR')}</Td>
                    <Td fontSize="xs" fontFamily="mono" display={{ base: 'none', md: 'table-cell' }}>{c.ip || '-'}</Td>
                    <Td fontSize="xs" color="gray.500" maxW="260px" isTruncated display={{ base: 'none', md: 'table-cell' }}>{c.user_agent || '-'}</Td>
                  </Tr>
                ))}
                {stats.dernieres_connexions.length === 0 && (
                  <Tr><Td colSpan={4}><Text color="gray.500" textAlign="center">Aucune donnée</Text></Td></Tr>
                )}
              </Tbody>
            </Table>
          </Box>
        </CardBody>
      </Card>
    </Box>
  );
}
